import { z } from "zod";
import { pinned } from "./pin";

export const CHAT_QUICK_REACTION_EMOJI_VALUES = ["👍", "❤️", "😂", "😮", "😢", "🙏"] as const;

export type ChatQuickReactionEmoji = (typeof CHAT_QUICK_REACTION_EMOJI_VALUES)[number];

export type ChatReactionEmoji = string;

const REACTION_EMOJI_MAX_LENGTH = 32;

const EMOJI_CLUSTER_ATOM =
  "(?:\\p{Extended_Pictographic}\\uFE0F?|\\p{Emoji_Presentation}|\\p{Emoji}\\uFE0F|\\p{Regional_Indicator}{2})";

let singleEmojiClusterRe: RegExp | null | undefined;

function getSingleEmojiClusterRe(): RegExp | null {
  if (singleEmojiClusterRe !== undefined) return singleEmojiClusterRe;
  try {
    singleEmojiClusterRe = new RegExp(
      `^${EMOJI_CLUSTER_ATOM}(?:\\u200D${EMOJI_CLUSTER_ATOM})*$`,
      "u",
    );
  } catch {
    singleEmojiClusterRe = null;
  }
  return singleEmojiClusterRe;
}

function isExtendedPictographic(segment: string): boolean {
  try {
    return new RegExp("\\p{Extended_Pictographic}", "u").test(segment);
  } catch {
    return false;
  }
}

function isFlagEmoji(segment: string): boolean {
  try {
    return new RegExp("^[\\p{Regional_Indicator}]{2}$", "u").test(segment);
  } catch {
    return false;
  }
}

function isSingleReactionEmojiViaSegmenter(value: string): boolean {
  try {
    const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });
    const segments = Array.from(segmenter.segment(value), (part) => part.segment);
    if (segments.length !== 1) return false;
    const segment = segments[0]!;
    return isExtendedPictographic(segment) || isFlagEmoji(segment);
  } catch {
    return false;
  }
}

export function isSingleReactionEmoji(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > REACTION_EMOJI_MAX_LENGTH) return false;
  const pattern = getSingleEmojiClusterRe();
  if (pattern?.test(trimmed)) return true;
  return isSingleReactionEmojiViaSegmenter(trimmed);
}

export const ChatReactionEmojiSchema = pinned<ChatReactionEmoji>()(
  z
    .string()
    .trim()
    .min(1)
    .max(REACTION_EMOJI_MAX_LENGTH)
    .refine(isSingleReactionEmoji, { message: "Invalid emoji" }),
);

export interface ChatReactionRequest {
  messageId: string;
  emoji: ChatReactionEmoji;
}

export const ChatReactionRequestSchema = pinned<ChatReactionRequest>()(
  z.object({
    messageId: z.string().uuid(),
    emoji: ChatReactionEmojiSchema,
  }),
);

export interface ChatReactionResponse {
  messageId: string;
  viewerEmoji: ChatReactionEmoji | null;
  counts: Record<string, number>;
}

export const ChatReactionResponseSchema = pinned<ChatReactionResponse>()(
  z.object({
    messageId: z.string().uuid(),
    viewerEmoji: ChatReactionEmojiSchema.nullable(),
    counts: z.record(z.string(), z.number()),
  }),
);
